import { isAnswerEmpty, type SurveyQuestion, type ValidationRule } from '../types.js';

// Structured failure shape used by the renderer to emit validation_failed
// events with the specific rule that fired. validateAnswer wraps this and
// returns just the message for back-compat with existing call sites.
export interface ValidationFailure {
  rule: string;   // 'required' | 'email' | 'url' | 'phone' | 'number' | 'regex' | 'minLength' | etc.
  message: string;
}

export function validateAnswer(question: SurveyQuestion, value: unknown): string | null {
  return validateAnswerDetailed(question, value)?.message ?? null;
}

export function validateAnswerDetailed(
  question: SurveyQuestion,
  value: unknown,
): ValidationFailure | null {
  const empty = isAnswerEmpty(value);
  if (question.required && empty) {
    return { rule: 'required', message: `"${question.label}" is required.` };
  }
  if (empty) return null;

  const builtin = validateBuiltinTypeDetailed(question, value);
  if (builtin) return builtin;

  for (const rule of question.validationRules ?? []) {
    const failure = applyRuleDetailed(rule, value);
    if (failure) return failure;
  }
  return null;
}

function validateBuiltinTypeDetailed(
  question: SurveyQuestion,
  value: unknown,
): ValidationFailure | null {
  switch (question.type) {
    case 'email':
      return isValidEmail(String(value)) ? null : { rule: 'email', message: 'Enter a valid email address.' };
    case 'website':
    case 'url':
      return isValidUrl(String(value)) ? null : { rule: 'url', message: 'Enter a valid URL.' };
    case 'phone':
      return isValidPhone(value) ? null : { rule: 'phone', message: 'Enter a valid phone number.' };
    case 'number':
      return Number.isFinite(Number(value)) ? null : { rule: 'number', message: 'Enter a valid number.' };
    default:
      return null;
  }
}

function applyRuleDetailed(rule: ValidationRule, value: unknown): ValidationFailure | null {
  switch (rule.type) {
    case 'regex': {
      const pattern = typeof rule.value === 'string' ? rule.value : '';
      if (!pattern) return null;
      try {
        return new RegExp(pattern).test(String(value))
          ? null
          : { rule: 'regex', message: rule.message };
      } catch {
        return null;
      }
    }
    case 'minLength':
      return lengthOf(value) >= toNumber(rule.value, 0)
        ? null
        : { rule: 'minLength', message: rule.message };
    case 'maxLength':
      return lengthOf(value) <= toNumber(rule.value, Infinity)
        ? null
        : { rule: 'maxLength', message: rule.message };
    case 'minValue':
      return toNumber(value, NaN) >= toNumber(rule.value, -Infinity)
        ? null
        : { rule: 'minValue', message: rule.message };
    case 'maxValue':
      return toNumber(value, NaN) <= toNumber(rule.value, Infinity)
        ? null
        : { rule: 'maxValue', message: rule.message };
    case 'email':
      return isValidEmail(String(value)) ? null : { rule: 'email', message: rule.message };
    case 'url':
      return isValidUrl(String(value)) ? null : { rule: 'url', message: rule.message };
    case 'custom':
      // Custom rules are evaluated server-side at submit time. We pass
      // through here so the user isn't blocked client-side by a rule we
      // can't reproduce (e.g. "value must be unique across responses").
      return null;
    default:
      return null;
  }
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(candidate);
    return /\./.test(url.hostname) && url.hostname.length > 3;
  } catch {
    return false;
  }
}

function isValidPhone(value: unknown): boolean {
  // Phone may arrive as a string (legacy) or as { code, number } from PhoneField.
  const raw =
    typeof value === 'string'
      ? value
      : value && typeof value === 'object'
        ? `${(value as { code?: string }).code ?? ''}${(value as { number?: string }).number ?? ''}`
        : '';
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (!/^[+\d\s\-().]+$/.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

function lengthOf(value: unknown): number {
  if (typeof value === 'string') return value.length;
  if (Array.isArray(value)) return value.length;
  if (value == null) return 0;
  return String(value).length;
}

function toNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
